import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { ReactNode } from 'react';

import { INTEGRATION_PROVIDER_ID } from '@/api/integration';
import AdrollLogo from '@/assets/svg/integration/adroll.svg?react';
import DemandbaseLogo from '@/assets/svg/integration/demandbase.svg?react';
import FacebookAdsLogo from '@/assets/svg/integration/facebook-ads.svg?react';
import G2Logo from '@/assets/svg/integration/g2.svg?react';
import GoogleAdManagerLogo from '@/assets/svg/integration/google-ad-manager.svg?react';
import GoogleAdsLogo from '@/assets/svg/integration/google-ads.svg?react';
import HubspotLogo from '@/assets/svg/integration/hubspot.svg?react';
import LinkedInLogo from '@/assets/svg/integration/linkedin.svg?react';
import MicrosoftAdsLogo from '@/assets/svg/integration/microsoft-ads.svg?react';
import RedditLogo from '@/assets/svg/integration/reddit.svg?react';
import RollworksLogo from '@/assets/svg/integration/rollworks.svg?react';
import SalesforceLogo from '@/assets/svg/integration/salesforce.svg?react';
import SlackLogo from '@/assets/svg/integration/slack.svg?react';
import FacebookSquareLogo from '@/assets/svg/integration/square/facebook.svg?react';
import GoogleAdsSquareLogo from '@/assets/svg/integration/square/google-ads.svg?react';
import LinkedInSquareLogo from '@/assets/svg/integration/square/linkedin.svg?react';
import MicrosoftSquareLogo from '@/assets/svg/integration/square/microsoft.svg?react';
import StackAdaptSquareLogo from '@/assets/svg/integration/square/stack-adapt.svg?react';
import StackAdaptLogo from '@/assets/svg/integration/stack-adapt.svg?react';
import TikTokLogo from '@/assets/svg/integration/tiktok.svg?react';
import XAdsLogo from '@/assets/svg/integration/x-ads.svg?react';
import { Link } from '@/components/typography';
import getSupportURL, { C99_SUPPORT_HOME_URL } from '@/constants/support-urls';

import type { AuthMethod } from './types';

export const getIsRecognizedProvider = (providerId?: INTEGRATION_PROVIDER_ID) => {
  return providerId && Object.values(INTEGRATION_PROVIDER_ID).includes(providerId);
};

export const getProviderAuthMethodById = (
  providerId?: INTEGRATION_PROVIDER_ID,
): AuthMethod | null => {
  switch (providerId) {
    case INTEGRATION_PROVIDER_ID.ADROLL_ADS:
      return { type: 'oauth' };
    case INTEGRATION_PROVIDER_ID.MICROSOFT_ADS:
      return { type: 'oauth' };
    case INTEGRATION_PROVIDER_ID.DEMANDBASE_ADS:
      return {
        type: 'api-token',
        title: t`Demandbase Token`,
        instructions: [
          t`Navigate to the Settings → API Key Sets page within the Demandbase Platform. Create a new connection named "Channel99" and then create a token inside that connection.`,
        ],
        fields: [
          { id: 'clientId', label: t`Client ID`, type: 'text' },
          { id: 'clientSecret', label: t`Client Secret`, type: 'text' },
        ],
      };
    case INTEGRATION_PROVIDER_ID.FACEBOOK_ADS:
      return { type: 'oauth' };
    case INTEGRATION_PROVIDER_ID.G2:
      return { type: 'oauth' };
    case INTEGRATION_PROVIDER_ID.GOOGLE_ADS:
      return { type: 'oauth' };
    case INTEGRATION_PROVIDER_ID.GOOGLE_AD_MANAGER:
      return { type: 'oauth' };
    case INTEGRATION_PROVIDER_ID.HUBSPOT:
      return { type: 'oauth' };
    case INTEGRATION_PROVIDER_ID.LINKEDIN:
      return { type: 'oauth' };
    case INTEGRATION_PROVIDER_ID.REDDIT_ADS:
      return { type: 'oauth' };
    case INTEGRATION_PROVIDER_ID.ROLLWORKS_ADS:
      return { type: 'oauth' };
    case INTEGRATION_PROVIDER_ID.SALESFORCE:
      return { type: 'oauth' };
    case INTEGRATION_PROVIDER_ID.SLACK:
      return { type: 'oauth' };
    case INTEGRATION_PROVIDER_ID.STACK_ADAPT:
      return {
        type: 'api-token',
        title: t`StackAdapt Token`,
        instructions: [
          t`Please place your StackAdapt provided token into the space below to connect.`,
        ],
        fields: [{ id: 'accessToken', label: t`Access Token`, type: 'text' }],
      };
    case INTEGRATION_PROVIDER_ID.TIKTOK_ADS:
      return { type: 'oauth' };
    case INTEGRATION_PROVIDER_ID.X_ADS:
      return { type: 'oauth' };
    default:
      return null;
  }
};

export const getProviderShortDescriptionById = (providerId?: INTEGRATION_PROVIDER_ID) => {
  if (!providerId) {
    return null;
  }

  switch (providerId) {
    case INTEGRATION_PROVIDER_ID.ADROLL_ADS:
      return t`Connect Adroll spend and results to make meaningful campaigns.`;
    case INTEGRATION_PROVIDER_ID.MICROSOFT_ADS:
      return t`Connect Microsoft ad spend and results to Channel99.`;
    case INTEGRATION_PROVIDER_ID.DEMANDBASE_ADS:
      return t`Connect Demandbase advertising spend and results to Channel99.`;
    case INTEGRATION_PROVIDER_ID.FACEBOOK_ADS:
      return t`Connect Facebook ad spend and results from Facebook Paid Social.`;
    case INTEGRATION_PROVIDER_ID.G2:
      return t`Connect G2 ad spend and results to Channel99.`;
    case INTEGRATION_PROVIDER_ID.GOOGLE_AD_MANAGER:
      return t`Connect Google Ad Manager for accounts which have paid access to the Google Ad Manager product.`;
    case INTEGRATION_PROVIDER_ID.GOOGLE_ADS:
      return t`Connect your ad spend and results from Search, Youtube and Display.`;
    case INTEGRATION_PROVIDER_ID.HUBSPOT:
      return t`Sync your Opportunities and Accounts from HubSpot.`;
    case INTEGRATION_PROVIDER_ID.LINKEDIN:
      return t`Connect your ad spend and Linkedin metrics for analysis.`;
    case INTEGRATION_PROVIDER_ID.REDDIT_ADS:
      return t`Connect Reddit ad spend and results to make meaningful campaigns.`;
    case INTEGRATION_PROVIDER_ID.ROLLWORKS_ADS:
      return t`Connect RollWorks spend and results to make meaningful campaigns.`;
    case INTEGRATION_PROVIDER_ID.SLACK:
      return t`Bring Channel99 intelligence into Slack—ask questions, get instant answers, and receive AI-powered digests.`;
    case INTEGRATION_PROVIDER_ID.SALESFORCE:
      return t`Sync your Opportunities and Accounts from Salesforce.`;
    case INTEGRATION_PROVIDER_ID.STACK_ADAPT:
      return t`Connect your StackAdapt for performance metrics and analysis.`;
    case INTEGRATION_PROVIDER_ID.TIKTOK_ADS:
      return t`Connect your ad spend and results from Tiktok to measure performance side-by-side with other vendors.`;
    case INTEGRATION_PROVIDER_ID.X_ADS:
      return t`Connect X Business (formerly Twitter) advertising spend and results to Channel99.`;
    default:
      return t`Unknown provider`;
  }
};

export const getProviderLongDescriptionById = (providerId?: INTEGRATION_PROVIDER_ID) => {
  switch (providerId) {
    case INTEGRATION_PROVIDER_ID.ADROLL_ADS:
      return [
        <Trans key={0}>
          Linking your AdRoll Ads account to Channel99 provides valuable insights into the
          effectiveness of Adroll Ads Display by automating Vendor Spend and delivery metrics for up
          to date ROI insights.
        </Trans>,
      ];
    case INTEGRATION_PROVIDER_ID.MICROSOFT_ADS:
      return [
        <Trans key={0}>
          Linking your Microsoft Ads account to Channel99 provides valuable insights into the
          effectiveness of Microsoft Ads Display and Paid Search by automating Vendor Spend and
          delivery metrics for up to date ROI insights.
        </Trans>,
      ];
    case INTEGRATION_PROVIDER_ID.DEMANDBASE_ADS:
      return [
        <Trans key={0}>
          Linking your Demandbase Ads account to Channel99 provides valuable insights into the
          effectiveness of Demandbase Ads Display by automating Vendor Spend and delivery metrics
          for up to date ROI insights.
        </Trans>,
        <Trans key={1}>
          You will need to login to your Demandbase account to retrieve your API token from the{' '}
          <Link variant="headline" to="https://web.demandbase.com/o/ad/at" target="_blank">
            API Tokens screen
          </Link>{' '}
          under Settings and API Tokens within the Demandbase platform. If you do not have access to
          this screen or the API has not been activated by your account manager, please reach out to
          your Demandbase representative to turn the API access for your token on.
        </Trans>,
      ];
    case INTEGRATION_PROVIDER_ID.FACEBOOK_ADS:
      return [
        <Trans key={0}>
          Linking your Facebook ads account to Channel99 provides valuable insights into the
          effectiveness of Facebook Paid Social ads by automating Vendor Spend and delivery metrics
          for up to date ROI insights.
        </Trans>,
      ];
    case INTEGRATION_PROVIDER_ID.G2:
      return [
        <Trans key={0}>
          Linking your G2 account to Channel99 provides valuable insights into the effectiveness of
          G2 by automating Vendor Spend and delivery metrics for up to date ROI insights.
        </Trans>,
      ];
    case INTEGRATION_PROVIDER_ID.GOOGLE_AD_MANAGER:
      return [
        <Trans key={0}>
          Linking your Google Ad Manager account to Channel99 provides valuable insights into the
          effectiveness of Google Display and Paid Search by automating Vendor Spend and delivery
          metrics for up to date ROI insights. This integration is only available to account which
          already own the Google Ad Manager product through Google.
        </Trans>,
      ];
    case INTEGRATION_PROVIDER_ID.GOOGLE_ADS:
      return [
        <Trans key={0}>
          Linking your Google Ads account to Channel99 provides valuable insights into the
          effectiveness of Google Display and Paid Search by automating Vendor Spend and delivery
          metrics for up to date ROI insights.
        </Trans>,
      ];
    case INTEGRATION_PROVIDER_ID.HUBSPOT:
      return [
        <Trans key={0}>
          Linking your HubSpot account to Channel99 provides valuable insights into the
          effectiveness of your channels and vendors by tying your investments to your
          opportunities. This means that you can track the success of your marketing efforts all the
          way from first engagement to closed deals, allowing you to understand which channels and
          vendors are most effective at driving revenue.
        </Trans>,
      ];
    case INTEGRATION_PROVIDER_ID.LINKEDIN:
      return [
        <Trans key={0}>
          Linking your LinkedIn account to Channel99 provides valuable insights into the
          effectiveness of LinkedIn by automating Vendor Spend so you always have accurate and up to
          date ROI insights.
        </Trans>,
      ];
    case INTEGRATION_PROVIDER_ID.REDDIT_ADS:
      return [
        <Trans key={0}>
          Linking your Reddit Ads account to Channel99 provides valuable insights into the
          effectiveness of Reddit Paid Social by automating Vendor Spend and delivery metrics for up
          to date ROI insights.
        </Trans>,
      ];
    case INTEGRATION_PROVIDER_ID.ROLLWORKS_ADS:
      return [
        <Trans key={0}>
          Linking your Rollworks Ads account to Channel99 provides valuable insights into the
          effectiveness of Rollworks Ads Display by automating Vendor Spend and delivery metrics for
          up to date ROI insights.
        </Trans>,
      ];
    case INTEGRATION_PROVIDER_ID.SALESFORCE:
      return [
        <Trans key={0}>
          Linking your Salesforce account to Channel99 provides valuable insights into the
          effectiveness of your channels and vendors by tying your investments to your
          opportunities. This means that you can track the success of your marketing efforts all the
          way from first engagement to closed deals, allowing you to understand which channels and
          vendors are most effective at driving revenue.
        </Trans>,
      ];
    case INTEGRATION_PROVIDER_ID.SLACK:
      return [
        <Trans key={0}>
          Linking your Slack workspace to Channel99 brings powerful AI assistance directly into the
          tools your teams use every day. By enabling the Channel99 Slack Bot, users can ask
          questions of their Channel99 data, retrieve helpful URLs to key areas of the platform, and
          access support resources such as help-center articles—all without leaving Slack.
        </Trans>,
        <Trans key={1}>
          In addition, Channel99 can automatically deliver periodic AI-generated digests right to
          your Slack channels, providing timely insights and recommendations from your marketing
          performance data. This ensures your team always stays informed and aligned around what's
          driving results.
        </Trans>,
      ];
    case INTEGRATION_PROVIDER_ID.STACK_ADAPT:
      return [
        <Trans key={0}>
          Connect your StackAdapt Ads account to Channel99 to automatically sync daily
          campaign-level spend and performance data.
        </Trans>,
        <Trans key={1}>
          Once connected, Channel99 will pull impressions, clicks, and spend directly from
          StackAdapt and apply tracking parameters to ensure accurate campaign attribution and ROI
          analysis no manual data entry required.
        </Trans>,
      ];
    case INTEGRATION_PROVIDER_ID.TIKTOK_ADS:
      return [
        <Trans key={0}>
          Linking your TikTok Ads account to Channel99 provides valuable insights into the
          effectiveness of TikTok Paid Social by automating Vendor Spend and delivery metrics for up
          to date ROI insights.
        </Trans>,
      ];
    case INTEGRATION_PROVIDER_ID.X_ADS:
      return [
        <Trans key={0}>
          Linking your X Ads account (formerly Twitter) to Channel99 provides valuable insights into
          the effectiveness of X Paid Social by automating Vendor Spend and delivery metrics for up
          to date ROI insights.
        </Trans>,
      ];
    default:
      return null;
  }
};

export const getProviderLogoById = (providerId?: INTEGRATION_PROVIDER_ID) => {
  switch (providerId) {
    case INTEGRATION_PROVIDER_ID.ADROLL_ADS:
      return <AdrollLogo />;
    case INTEGRATION_PROVIDER_ID.MICROSOFT_ADS:
      return <MicrosoftAdsLogo />;
    case INTEGRATION_PROVIDER_ID.DEMANDBASE_ADS:
      return <DemandbaseLogo />;
    case INTEGRATION_PROVIDER_ID.FACEBOOK_ADS:
      return <FacebookAdsLogo />;
    case INTEGRATION_PROVIDER_ID.G2:
      return <G2Logo />;
    case INTEGRATION_PROVIDER_ID.GOOGLE_AD_MANAGER:
      return <GoogleAdManagerLogo />;
    case INTEGRATION_PROVIDER_ID.GOOGLE_ADS:
      return <GoogleAdsLogo />;
    case INTEGRATION_PROVIDER_ID.HUBSPOT:
      return <HubspotLogo />;
    case INTEGRATION_PROVIDER_ID.LINKEDIN:
      return <LinkedInLogo />;
    case INTEGRATION_PROVIDER_ID.REDDIT_ADS:
      return <RedditLogo />;
    case INTEGRATION_PROVIDER_ID.ROLLWORKS_ADS:
      return <RollworksLogo />;
    case INTEGRATION_PROVIDER_ID.SALESFORCE:
      return <SalesforceLogo />;
    case INTEGRATION_PROVIDER_ID.SLACK:
      return <SlackLogo />;
    case INTEGRATION_PROVIDER_ID.STACK_ADAPT:
      return <StackAdaptLogo />;
    case INTEGRATION_PROVIDER_ID.TIKTOK_ADS:
      return <TikTokLogo />;
    case INTEGRATION_PROVIDER_ID.X_ADS:
      return <XAdsLogo />;
    default:
      return null;
  }
};

export const getActivationProviderLogoMap = (): Record<string, ReactNode> => {
  return {
    [INTEGRATION_PROVIDER_ID.MICROSOFT_ADS]: <MicrosoftSquareLogo />,
    [INTEGRATION_PROVIDER_ID.FACEBOOK_ADS]: <FacebookSquareLogo />,
    [INTEGRATION_PROVIDER_ID.GOOGLE_ADS]: <GoogleAdsSquareLogo />,
    [INTEGRATION_PROVIDER_ID.LINKEDIN]: <LinkedInSquareLogo />,
    [INTEGRATION_PROVIDER_ID.STACK_ADAPT]: <StackAdaptSquareLogo />,
  };
};

export const getProviderSquareLogoById = (providerId?: INTEGRATION_PROVIDER_ID) => {
  if (!providerId) {
    return null;
  }

  const logoMap = getActivationProviderLogoMap();

  if (logoMap[providerId]) {
    return logoMap[providerId];
  }

  return t`No logo`;
};

export const getProviderHelpLinkById = (providerId?: INTEGRATION_PROVIDER_ID) => {
  switch (providerId) {
    case INTEGRATION_PROVIDER_ID.ADROLL_ADS:
      return getSupportURL('INTEGRATION_ADROLL');
    case INTEGRATION_PROVIDER_ID.MICROSOFT_ADS:
      return getSupportURL('INTEGRATION_MICROSOFT');
    case INTEGRATION_PROVIDER_ID.DEMANDBASE_ADS:
      return getSupportURL('INTEGRATION_DEMANDBASE');
    case INTEGRATION_PROVIDER_ID.FACEBOOK_ADS:
      return getSupportURL('INTEGRATION_FACEBOOK');
    case INTEGRATION_PROVIDER_ID.G2:
      return getSupportURL('INTEGRATION_G2');
    case INTEGRATION_PROVIDER_ID.GOOGLE_ADS:
      return getSupportURL('INTEGRATION_GOOGLE');
    case INTEGRATION_PROVIDER_ID.GOOGLE_AD_MANAGER:
      return getSupportURL('INTEGRATION_GOOGLE_AD_MANAGER');
    case INTEGRATION_PROVIDER_ID.LINKEDIN:
      return getSupportURL('INTEGRATION_LINKEDIN');
    case INTEGRATION_PROVIDER_ID.REDDIT_ADS:
      return getSupportURL('INTEGRATION_REDDIT');
    case INTEGRATION_PROVIDER_ID.STACK_ADAPT:
      return getSupportURL('INTEGRATION_STACK_ADAPT');
    case INTEGRATION_PROVIDER_ID.SALESFORCE:
      return getSupportURL('INTEGRATION_SALESFORCE');
    case INTEGRATION_PROVIDER_ID.TIKTOK_ADS:
      return getSupportURL('INTEGRATION_TIKTOK');
    case INTEGRATION_PROVIDER_ID.X_ADS:
      return getSupportURL('INTEGRATION_X');
    default:
      return C99_SUPPORT_HOME_URL;
  }
};
